// Initialize Sentry before any other imports
import { initSentry } from "@/services/sentry";
initSentry();

import { client, theme } from "@/configs/app";
import { logger } from "@/utils/debug";
import "@mantine/carousel/styles.css";
import { MantineProvider } from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from "react-router";
import { AppErrorBoundary, AppInitializer } from "./features/app";
import "./index.css";
import {
  BuildLayout,
  DiscoverLayout,
  DiscoverResultsLayout,
  MainLayout,
  ProjectsLayout,
} from "./layouts";
import { BuildPage, DataflowPage, ProjectPage } from "./pages/build";
import {
  DiscoverPage,
  DiscoverResultsDetailsPage,
  DiscoverResultsPage,
  DiscoverSectionPage,
} from "./pages/discover";
import { LoginPage } from "./pages/login";
import { MyDataPage } from "./pages/my-data";
import { ProfilePage } from "./pages/profile";
import { clearOldStores, listAllStores } from "./services/storage";

ModuleRegistry.registerModules([AllCommunityModule]);

function Root() {
  return (
    <QueryClientProvider client={client}>
      <MantineProvider theme={theme}>
        <AppInitializer>
          <Outlet />
        </AppInitializer>
      </MantineProvider>
    </QueryClientProvider>
  );
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    children: [
      {
        index: true,
        element: <Navigate to="/discover" replace />,
      },
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        element: <MainLayout />,
        children: [
          {
            path: "discover",
            element: <DiscoverLayout />,
            children: [
              {
                index: true,
                element: <DiscoverPage />,
              },
              {
                path: ":section",
                element: <DiscoverSectionPage />,
              },
            ],
          },
          {
            path: "discover/search",
            element: <DiscoverResultsLayout />,
            children: [
              {
                index: true,
                element: <DiscoverResultsPage />,
              },
            ],
          },
          {
            path: "discover/dataset/:datasetId",
            element: <DiscoverResultsDetailsPage />,
          },
          {
            path: "build",
            element: <BuildLayout />,
            children: [
              {
                element: <ProjectsLayout />,
                children: [
                  {
                    index: true,
                    element: <BuildPage />,
                  },
                  {
                    path: ":projectId",
                    element: <ProjectPage />,
                  },
                ],
              },
              {
                path: ":projectId/:dataflowId",
                element: <DataflowPage />,
              },
            ],
          },
          {
            path: "my-data",
            element: <MyDataPage />,
          },
          {
            path: "profile",
            element: <ProfilePage />,
          },
        ],
      },
    ],
  },
]);

if (typeof window !== "undefined") {
  (window as any).atlasio = {
    /**
     * Clear old/unused IndexedDB stores
     * Usage: await atlasio.clearDatabase()
     */
    clearDatabase: clearOldStores,
    /**
     * List all IndexedDB stores in the atlasio-store database
     * Usage: await atlasio.listStores()
     */
    listStores: listAllStores,
  };

  logger.group("Atlasio Debug Tools");
  logger.log(`await atlasio.clearDatabase() \t Clear old IndexedDB stores`);
  logger.log(`await atlasio.listStores() \t\t\t List all IndexedDB stores`);
  logger.groupEnd();
}

createRoot(document.getElementById("root")!).render(
  <AppErrorBoundary>
    <RouterProvider router={router} />
  </AppErrorBoundary>
);
